import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 1rem;
`;

const Section = styled.section`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const ViewAllLink = styled(Link)`
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  color: #333;
  font-weight: 500;
  border-bottom: 1px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'draft':
        return 'background: #e9ecef; color: #6c757d;';
      case 'submitted':
        return 'background: #fff3cd; color: #856404;';
      case 'awaiting_payment':
        return 'background: #d1ecf1; color: #0c5460;';
      case 'payment_verification':
        return 'background: #e2e3e5; color: #383d41;';
      case 'in_progress':
        return 'background: #cce5ff; color: #004085;';
      case 'awaiting_documents':
        return 'background: #f8d7da; color: #721c24;';
      case 'awaiting_signatures':
        return 'background: #f8d7da; color: #721c24;';
      case 'completed':
        return 'background: #d4edda; color: #155724;';
      case 'rejected':
        return 'background: #f8d7da; color: #721c24;';
      default:
        return 'background: #e9ecef; color: #6c757d;';
    }
  }}
`;

interface DashboardData {
  status_counts: Record<string, number>;
  recent_registrations: Array<{
    id: number;
    user_email: string;
    company_name_armenian: string;
    status: string;
    created_at: string;
  }>;
  total_registrations: number;
  total_users: number;
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('Fetching dashboard data from:', getCurrentApiEndpoints().ADMIN_DASHBOARD);
        const response = await axios.get(getCurrentApiEndpoints().ADMIN_DASHBOARD);
        console.log('Dashboard data received:', response.data);
        setDashboardData(response.data);
      } catch (error: any) {
        console.error('Failed to fetch dashboard data:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error headers:', error.response?.headers);
        
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
        setError(`Failed to load dashboard data: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (isLoading) {
    return <div>Loading dashboard...</div>;
  }

  if (error) {
    return (
      <DashboardContainer>
        <Title>Admin Dashboard</Title>
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '4px',
          marginBottom: '2rem'
        }}>
          {error}
        </div>
      </DashboardContainer>
    );
  }

  if (!dashboardData) {
    return (
      <DashboardContainer>
        <Title>Admin Dashboard</Title>
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '4px',
          marginBottom: '2rem'
        }}>
          Failed to load dashboard data
        </div>
      </DashboardContainer>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'payment_verification': return 'Payment Verification';
      case 'in_progress': return 'In Progress';
      case 'awaiting_documents': return 'Awaiting Documents';
      case 'awaiting_signatures': return 'Awaiting Signatures';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  return (
    <DashboardContainer>
      <Title>Admin Dashboard</Title>
      
      <StatsGrid>
        <StatCard>
          <StatNumber>{dashboardData.total_registrations}</StatNumber>
          <StatLabel>Total Registrations</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{dashboardData.total_users}</StatNumber>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{dashboardData.status_counts.submitted || 0}</StatNumber>
          <StatLabel>Pending Review</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{dashboardData.status_counts.awaiting_payment || 0}</StatNumber>
          <StatLabel>Awaiting Payment</StatLabel>
        </StatCard>
      </StatsGrid>

      <Section>
        <SectionHeader>
          <SectionTitle>Registration Status Overview</SectionTitle>
        </SectionHeader>
        <Table>
          <thead>
            <tr>
              <TableHeader>Status</TableHeader>
              <TableHeader>Count</TableHeader>
            </tr>
          </thead>
          <tbody>
            {Object.entries(dashboardData.status_counts).map(([status, count]) => (
              <tr key={status}>
                <TableCell>
                  <StatusBadge status={status}>
                    {getStatusLabel(status)}
                  </StatusBadge>
                </TableCell>
                <TableCell>{count}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Recent Registrations</SectionTitle>
          <ViewAllLink to="/admin/registrations">View All</ViewAllLink>
        </SectionHeader>
        <Table>
          <thead>
            <tr>
              <TableHeader>Company Name</TableHeader>
              <TableHeader>User Email</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
            </tr>
          </thead>
          <tbody>
            {dashboardData.recent_registrations.map((registration) => (
              <tr key={registration.id}>
                <TableCell>
                  <Link to={`/admin/registrations/${registration.id}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                    {registration.company_name_armenian || 'N/A'}
                  </Link>
                </TableCell>
                <TableCell>{registration.user_email}</TableCell>
                <TableCell>
                  <StatusBadge status={registration.status}>
                    {getStatusLabel(registration.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell>{formatDate(registration.created_at)}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>
    </DashboardContainer>
  );
};

export default AdminDashboard;